import type {
	LinksFunction,
	MetaFunction,
	ActionFunctionArgs,
} from '@remix-run/node';
import {
	Form,
	useActionData,
	useLoaderData,
	useNavigation,
} from '@remix-run/react';

import { Banner } from '~/components/atoms/Banner/index.tsx';
import divider from '~/components/atoms/Divider/index.tsx';
import { Input, Textarea } from '~/components/atoms/FormFields/index.tsx';

import contentStylesUrl from '~/styles/content/content.css?url';
import stylesUrl from '~/styles/account/account.css?url';

export const links: LinksFunction = () => {
	return [
		{
			rel: 'stylesheet',
			href: contentStylesUrl,
		},
		{
			rel: 'stylesheet',
			href: stylesUrl,
		},
	];
};

export const meta: MetaFunction = () => [
	{
		title: 'GList | Contact us',
	},
];

type TLoaderData = {
	divider: string;
};

export const loader = async (): Promise<TLoaderData> => {
	const theDivider = divider();
	return {
		divider: theDivider,
	};
};

type TActionData = {
	formError?: string;
	fields?: {
		name?: string;
		enquiry?: string;
	};
	fieldErrors?: {
		name?: string;
		enquiry?: string;
	};
};

export const action = async ({
	request,
}: ActionFunctionArgs): Promise<TActionData> => {
	const form = await request.formData();
	const { name, enquiry } = Object.fromEntries(form.entries());

	if (typeof name !== 'string' || typeof enquiry !== 'string') {
		return { formError: 'Form submitted incorrectly' };
	}

	const fieldErrors = {
		name: name.trim() === '' ? 'Name is required' : undefined,
		enquiry: enquiry.trim() === '' ? 'Enquiry is required' : undefined,
	};

	if (Object.values(fieldErrors).some(Boolean)) {
		return { fieldErrors, fields: { name, enquiry } };
	}

	return {};
};

export default function Contact(): React.ReactNode {
	const actionData = useActionData<TActionData>();
	const { divider } = useLoaderData<TLoaderData>();
	const transition = useNavigation();

	const disabled =
		transition.state === 'submitting' || transition.state === 'loading';

	return (
		<main id="main">
			<Banner title="Contact Us" />
			<div
				className="divider white"
				role="img"
				aria-label="Mountain-scape divider"
				aria-hidden="true"
				dangerouslySetInnerHTML={{ __html: divider }}
			/>
			<section className="container small-container">
				<p>
					Please fill in the following form with any enquiries about
					us or our services and we'll endeavour to get back to you
					asap. Alternatively, you find us on{' '}
					<a
						href="https://twitter.com/ahallicks"
						rel="external noopener"
					>
						Twitter
					</a>
				</p>
				<Form method="post" className="account-form">
					<fieldset disabled={disabled}>
						<legend className="sr-only">Enquiry details</legend>
						<Input
							id="name-input"
							label="Name"
							type="text"
							name="name"
							actionData={actionData?.fieldErrors?.name}
							defaultValue={actionData?.fields?.name}
						/>
						<Textarea
							id="enquiry"
							label="Enquiry"
							name="enquiry"
							rows={5}
							actionData={actionData?.fieldErrors?.enquiry}
							defaultValue={actionData?.fields?.enquiry}
						/>
					</fieldset>
					{actionData?.formError ? (
						<div id="form-error-message">
							<p className="form-validation-error" role="alert">
								{actionData.formError}
							</p>
						</div>
					) : null}
					<div className="form-footer">
						<button
							type="submit"
							className="button"
							disabled={disabled}
						>
							Send Message
						</button>
					</div>
				</Form>
			</section>
		</main>
	);
}
